/**
 * FEAT-APR08-003 — My Gyms (multi-gym ownership)
 *
 * Lists every gym the current user owns, lets them switch between them,
 * and provides an "Add another gym" CTA. Each gym has its own subscription
 * status and bills independently.
 */

import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useApi } from '@/hooks/useApi';
import { getProfileDashboardPath } from '@/lib/authRouting';
import { useAuthStore } from '@/stores/authStore';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Loader2, Plus, Users } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

interface OwnedGym {
  id: string;
  name: string;
  subscription_status: string | null;
  created_at: string;
  profile_id: string | null;
  member_count: number;
  coach_count: number;
}

const statusColors: Record<string, string> = {
  active: 'bg-green-500/10 text-green-500 border-green-500/20',
  trialing: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  pending: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
  past_due: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
  cancelled: 'bg-red-500/10 text-red-500 border-red-500/20',
  paused: 'bg-gray-500/10 text-gray-500 border-gray-500/20',
};

export default function MyGyms() {
  const { api } = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { switchProfile, activeProfile, profiles } = useAuthStore();

  const [showAddModal, setShowAddModal] = useState(false);
  const [newName, setNewName] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['my-gyms'],
    queryFn: async () => {
      const res = await api.get<{ gyms: OwnedGym[] }>('/affiliates/mine');
      if (!res.success) throw new Error(res.error);
      return res.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (name: string) => {
      const res = await api.post<{
        affiliate: { id: string; name: string };
        profile_id: string;
      }>('/affiliates/mine', { name });
      if (!res.success) throw new Error(res.error);
      return res.data;
    },
    onSuccess: async (created) => {
      if (!created) return;
      toast.success(`${created.affiliate.name} created`);
      queryClient.invalidateQueries({ queryKey: ['my-gyms'] });
      setShowAddModal(false);
      setNewName('');
      // Switch into the new gym immediately
      if (created.profile_id) {
        try {
          await switchProfile(created.profile_id);
          navigate('/affiliate');
        } catch (e) {
          console.error('switchProfile failed after gym create', e);
        }
      }
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Could not create gym');
    },
  });

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex h-[40vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-destructive">Failed to load gyms</p>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  const gyms = data?.gyms || [];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <Building2 className="h-7 w-7 text-primary" />
              My Gyms
            </h1>
            <p className="text-muted-foreground">
              Each gym has its own subscription, members, and programming. Switch between gyms
              without logging out.
            </p>
          </div>
          <Button onClick={() => setShowAddModal(true)} className="shrink-0">
            <Plus className="h-4 w-4 mr-2" />
            Add another gym
          </Button>
        </div>

        <div className="grid gap-4">
          {gyms.map((g) => {
            const isActive = activeProfile?.affiliate_id === g.id;
            const statusClass =
              statusColors[g.subscription_status || 'pending'] || statusColors.pending;
            return (
              <Card key={g.id} className={isActive ? 'border-primary' : ''}>
                <CardHeader>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        {g.name}
                        {isActive && (
                          <Badge variant="outline" className="text-xs">
                            Current
                          </Badge>
                        )}
                      </CardTitle>
                      <CardDescription className="flex items-center gap-3 mt-1">
                        <span className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {g.member_count} members · {g.coach_count} coaches
                        </span>
                      </CardDescription>
                    </div>
                    <Badge variant="outline" className={statusClass}>
                      {g.subscription_status || 'pending'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2">
                    {!isActive && g.profile_id && (
                      <Button
                        size="sm"
                        onClick={async () => {
                          try {
                            const targetProfile = profiles.find((p) => p.id === g.profile_id);
                            await switchProfile(g.profile_id as string);
                            navigate(
                              targetProfile ? getProfileDashboardPath(targetProfile) : '/affiliate'
                            );
                          } catch (e) {
                            toast.error('Could not switch gym');
                            console.error(e);
                          }
                        }}
                      >
                        Switch to this gym
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => navigate('/billing')}>
                      Manage billing
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {showAddModal && (
          <dialog
            open
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={() => setShowAddModal(false)}
            onKeyDown={(e) => e.key === 'Escape' && setShowAddModal(false)}
            aria-modal="true"
          >
            <Card
              className="w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <CardHeader>
                <CardTitle>Add another gym</CardTitle>
                <CardDescription>
                  You'll start in freemium mode. Subscribe any time to unlock premium features for
                  this gym.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label htmlFor="gym-name" className="text-sm font-medium mb-2 block">
                    Gym name
                  </label>
                  <input
                    id="gym-name"
                    type="text"
                    className="w-full rounded-md border border-border bg-background p-2 text-sm"
                    placeholder="e.g. Box West"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setShowAddModal(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={() => createMutation.mutate(newName.trim())}
                    disabled={createMutation.isPending || newName.trim().length < 2}
                  >
                    Create gym
                  </Button>
                </div>
              </CardContent>
            </Card>
          </dialog>
        )}
      </div>
    </DashboardLayout>
  );
}
